import React, { createContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

export const AuthContext = createContext(null);

const STORAGE_KEY = 'admin_token';
const USER_KEY = 'admin_user';
const SESSION_TOKEN_KEY = 'admin_token_session';
const SESSION_USER_KEY = 'admin_user_session';

function getStorage(remember) {
  return remember ? localStorage : sessionStorage;
}

function loadFromBoth() {
  let token = localStorage.getItem(STORAGE_KEY);
  let userStr = localStorage.getItem(USER_KEY);
  let source = 'local';
  if (!token || !userStr) {
    token = sessionStorage.getItem(SESSION_TOKEN_KEY);
    userStr = sessionStorage.getItem(SESSION_USER_KEY);
    source = 'session';
  }
  if (token && userStr) {
    try {
      return { token, user: JSON.parse(userStr), source };
    } catch {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(USER_KEY);
      sessionStorage.removeItem(SESSION_TOKEN_KEY);
      sessionStorage.removeItem(SESSION_USER_KEY);
    }
  }
  return null;
}

function clearBoth() {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(USER_KEY);
  sessionStorage.removeItem(SESSION_TOKEN_KEY);
  sessionStorage.removeItem(SESSION_USER_KEY);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const saved = loadFromBoth();
    if (saved) {
      setToken(saved.token);
      setUser(saved.user);
    }
    setLoading(false);
  }, []);

  const login = useCallback(async (email, password, rememberMe = true) => {
    const response = await api.post('/auth/login', { email, password });
    const { token: newToken, userId, email: userEmail, firstName, lastName, role } = response.data;
    if (role?.toLowerCase() !== 'admin') {
      throw new Error('Access denied. Admin privileges required.');
    }
    const userData = { id: userId, email: userEmail, firstName, lastName, role };
    const storage = getStorage(rememberMe);
    const tokenKey = rememberMe ? STORAGE_KEY : SESSION_TOKEN_KEY;
    const userKey = rememberMe ? USER_KEY : SESSION_USER_KEY;
    clearBoth();
    storage.setItem(tokenKey, newToken);
    storage.setItem(userKey, JSON.stringify(userData));
    setToken(newToken);
    setUser(userData);
    return userData;
  }, []);

  const logout = useCallback(() => {
    clearBoth();
    setToken(null);
    setUser(null);
  }, []);

  const isAdmin = useCallback(() => {
    return user?.role?.toLowerCase() === 'admin';
  }, [user]);

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    isAdmin,
    isAuthenticated: !!token && !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
